import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { apiClient, extractApiError } from '../../../api/axios';
import { API } from '../../../constants/api';
import { DEFAULT_PAGE_SIZE } from '../../../constants/ui';

const initialNewOrderUserInfo = {
  phone_number: '+998 ',
  additional_number: '',
  fullname: '',
  region: '',
  district: '',
  address: '',
  delivery_floor: '',
  room_number: '',
  entrance_way: '',
  elevator_available: false,
  is_old_client: false,
  old_client_return_bottles: 0,
  customer: null,
  localOrder: null,
};

const initialState = {
  openMenu: null,
  openCollapse: null,
  isModalOpen: null,
  newOrderModal: null,
  collapseId: null,
  tab: '',
  page: 1,
  order: 'asc',
  selected: [],
  orderBy: 'name',
  rowsPerPage: DEFAULT_PAGE_SIZE,
  itemsCount: 0,
  pageSize: DEFAULT_PAGE_SIZE,
  total: null,

  loading: false,
  error: '',
  success: false,
  deleted: false,
  orders: [],

  postOrderLoading: false,
  postOrderSuccess: false,
  patchOrderSuccess: false,
  postOrderError: null,

  isOrderModalOpen: false,
  orderStatus: '',
  orderId: null,
  orderQuantity: 0,

  toOrderCount: 0,
  OrderAcceptedCount: 0,
  OrderRejectedCount: 0,
  DeliveringCount: 0,
  DoneCount: 0,
  CancelledCount: 0,

  markStatus: '',
  ordersCount: 0,

  orderDetailsModal: false,
  orderDetails: null,
  orderDetailsLoading: false,
  orderDetailsError: null,

  totalPrice: 0,

  newOrderUserInfo: initialNewOrderUserInfo,

  newOrderCart: [],
  promotion: localStorage.getItem('promotion_id') || null,
  localPromotions: [],
  orderUser: null,
  location: { latitude: null, longitude: null },
};

export const fetchOrders = createAsyncThunk('orders/fetchOrders', async (filter, { rejectWithValue }) => {
  try {
    const response = await apiClient.get(API.orders.list, { params: filter });
    return response.data;
  } catch (error) {
    return rejectWithValue(extractApiError(error));
  }
});

export const postOrders = createAsyncThunk('orders/postOrders', async (orderData, { rejectWithValue }) => {
  try {
    const response = await apiClient.post(API.orders.list, orderData);
    return response.data;
  } catch (error) {
    return rejectWithValue(extractApiError(error));
  }
});

export const patchOrder = createAsyncThunk('orders/patchOrder', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await apiClient.patch(API.orders.byId(id), data);
    return response.data;
  } catch (error) {
    return rejectWithValue(extractApiError(error));
  }
});

export const deleteOrders = createAsyncThunk('orders/deleteOrders', async ({ url }, { rejectWithValue }) => {
  try {
    const response = await apiClient.del(url);
    return response.data;
  } catch (error) {
    return rejectWithValue(extractApiError(error));
  }
});

export const orderAccept = createAsyncThunk('orders/orderAccept', async ({ id }, { rejectWithValue }) => {
  try {
    const response = await apiClient.patch(API.orders.acceptOrder(id));
    return response.data;
  } catch (error) {
    return rejectWithValue(extractApiError(error));
  }
});

export const markDelivering = createAsyncThunk('orders/markDelivering', async ({ id }, { rejectWithValue }) => {
  try {
    const response = await apiClient.patch(API.orders.markDelivering(id));
    return response.data;
  } catch (error) {
    return rejectWithValue(extractApiError(error));
  }
});

export const markDelivered = createAsyncThunk('orders/markDelivered', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await apiClient.patch(API.orders.markDelivered(id), data);
    return response.data;
  } catch (error) {
    return rejectWithValue(extractApiError(error));
  }
});

export const markChange = createAsyncThunk('orders/markChange', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await apiClient.patch(API.orders.byId(id), data);
    return response.data;
  } catch (error) {
    return rejectWithValue(error?.response?.data?.message?.[0] || extractApiError(error));
  }
});

export const markCancelled = createAsyncThunk('orders/markCancelled', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await apiClient.patch(API.orders.cancelOrder(id), data);
    return response.data;
  } catch (error) {
    return rejectWithValue(extractApiError(error));
  }
});

export const fetchOrderDetails = createAsyncThunk('orders/fetchOrderDetails', async (orderId, { rejectWithValue }) => {
  try {
    const response = await apiClient.get(API.orders.byId(orderId));
    return response.data;
  } catch (error) {
    return rejectWithValue(extractApiError(error));
  }
});

export const ordersSlice = createSlice({
  name: 'orders',
  initialState,

  extraReducers: (builder) => {
    builder.addCase(fetchOrders.pending, (state) => {
      state.loading = true;
      state.error = '';
      state.success = false;
    });
    builder.addCase(fetchOrders.fulfilled, (state, action) => {
      const orders = action.payload.results;
      const currentStatus = action.meta.arg?.status;

      if (currentStatus === 'pending') {
        state.toOrderCount = orders.length;
        state.ordersCount = orders.length;
      } else if (currentStatus === 'accepted') {
        state.OrderAcceptedCount = orders.length;
      } else if (currentStatus === 'rejected') {
        state.OrderRejectedCount = orders.length;
      } else if (currentStatus === 'delivering') {
        state.DeliveringCount = orders.length;
      } else if (currentStatus === 'finished') {
        state.DoneCount = orders.length;
      } else if (currentStatus === 'cancelled') {
        state.CancelledCount = orders.length;
      } else {
        const ordersCount = orders.filter((order) => order.status === 'pending').length;
        state.toOrderCount = ordersCount;
        state.OrderAcceptedCount = orders.filter((order) => order.status === 'accepted').length;
        state.OrderRejectedCount = orders.filter((order) => order.status === 'rejected').length;
        state.CancelledCount = orders.filter((order) => order.status === 'cancelled').length;
        state.DeliveringCount = orders.filter((order) => order.status === 'delivering').length;
        state.DoneCount = orders.filter((order) => order.status === 'finished').length;
        state.ordersCount = ordersCount;
      }
      state.loading = false;
      state.page = action.payload.page;
      state.pageSize = action.payload.page_size;
      state.total = action.payload.total;
      state.orders = orders;
      state.success = true;
    });
    builder.addCase(fetchOrders.rejected, (state, action) => {
      state.error = action.payload;
      if (action.meta?.arg?.page === 1) {
        toast.error(action.payload);
      }
      state.loading = false;
      state.success = false;
    });

    builder.addCase(postOrders.pending, (state) => {
      state.postOrderLoading = true;
      state.postOrderSuccess = false;
      state.postOrderError = null;
    });
    builder.addCase(postOrders.fulfilled, (state) => {
      state.postOrderLoading = false;
      state.postOrderSuccess = true;
      state.postOrderError = null;
    });
    builder.addCase(postOrders.rejected, (state, action) => {
      state.postOrderLoading = false;
      state.postOrderSuccess = false;
      state.postOrderError = action.payload;
    });

    builder.addCase(patchOrder.fulfilled, (state) => {
      state.patchOrderSuccess = true;
    });

    builder.addCase(deleteOrders.fulfilled, (state) => {
      state.deleted = true;
    });
    builder
      .addCase(deleteOrders.rejected, (state, action) => {
        toast.error(action.payload);
      })

      .addCase(orderAccept.pending, (state) => {
        state.loading = true;
      })
      .addCase(orderAccept.fulfilled, (state) => {
        state.loading = false;
        toast.success('Buyurtma qabul qilindi');
      })
      .addCase(orderAccept.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload);
      })

      .addCase(markDelivering.pending, (state) => {
        state.loading = true;
      })
      .addCase(markDelivering.fulfilled, (state) => {
        state.loading = false;
        toast.success('Yetkazishga belgilandi');
      })
      .addCase(markDelivering.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload);
      })

      .addCase(markDelivered.pending, (state) => {
        state.loading = true;
      })
      .addCase(markDelivered.fulfilled, (state) => {
        state.loading = false;
        toast.success('Yetkazildi deb belgilandi');
      })
      .addCase(markDelivered.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload);
      })

      .addCase(markChange.pending, (state) => {
        state.loading = true;
      })
      .addCase(markChange.fulfilled, (state, action) => {
        state.loading = false;
        state.localOrder = action.payload;
        toast.success(action.payload.message);
      })
      .addCase(markChange.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload);
      })

      .addCase(markCancelled.pending, (state) => {
        state.loading = true;
      })
      .addCase(markCancelled.fulfilled, (state) => {
        state.loading = false;
        toast.success('Bekor qilindi');
      })
      .addCase(markCancelled.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload);
      })

      .addCase(fetchOrderDetails.pending, (state) => {
        state.orderDetailsLoading = true;
        state.orderDetailsError = null;
      })
      .addCase(fetchOrderDetails.fulfilled, (state, action) => {
        state.orderDetailsLoading = false;
        state.orderDetails = action.payload;
      })
      .addCase(fetchOrderDetails.rejected, (state, action) => {
        state.orderDetailsLoading = false;
        state.orderDetailsError = action.payload;
      });
  },

  reducers: {
    setClearLocalOrder: (state) => {
      state.localOrder = null;
    },
    setTotalPrice: (state, { payload }) => {
      state.totalPrice = payload;
    },
    setOrderUser: (state, { payload }) => {
      state.orderUser = payload;
    },
    setOrdersCount: (state, { payload }) => {
      state.ordersCount = payload;
    },
    setOpenMenu: (state, { payload }) => {
      state.openMenu = payload;
    },
    setIsOrderModalOpen: (state, { payload }) => {
      state.isOrderModalOpen = payload;
    },
    setOrderStatus: (state, { payload }) => {
      state.orderStatus = payload;
    },
    setOrderId: (state, { payload }) => {
      state.orderId = payload;
    },
    setOrderQuantity: (state, { payload }) => {
      state.orderQuantity = payload;
    },
    setOpenCollapse: (state, { payload }) => {
      state.openCollapse = payload;
    },
    setIsModalOpen: (state, { payload }) => {
      state.isModalOpen = payload;
    },
    setCollapseId: (state, { payload }) => {
      state.collapseId = payload;
    },
    setTab: (state, { payload }) => {
      state.tab = payload;
    },
    setPage: (state, { payload }) => {
      state.page = payload;
    },
    setOrder: (state, { payload }) => {
      state.order = payload;
    },
    setSelected: (state, { payload }) => {
      state.selected = payload;
    },
    setOrderBy: (state, { payload }) => {
      state.orderBy = payload;
    },
    setRowsPerPage: (state, { payload }) => {
      state.rowsPerPage = payload;
    },
    setNewOrderModal: (state, { payload }) => {
      state.newOrderModal = payload;
    },
    setDefaults: (state) => {
      state.success = false;
      state.postOrderSuccess = false;
      state.patchOrderSuccess = false;
      state.error = '';
    },
    setOrderDetailsModal: (state, { payload }) => {
      state.orderDetailsModal = payload;
      if (!payload) {
        state.orderDetails = null;
        state.orderDetailsError = null;
      }
    },
    setNewOrderUserInfo: (state, { payload }) => {
      state.newOrderUserInfo = { ...state.newOrderUserInfo, ...payload };
    },
    clearNewOrderUserInfo: (state) => {
      state.newOrderUserInfo = { ...initialNewOrderUserInfo };
      state.newOrderCart = [];
    },
    setNewOrderCart: (state, { payload }) => {
      state.newOrderCart = payload;
    },
    addToNewOrderCart: (state, { payload }) => {
      const existingItem = state.newOrderCart.find((item) => item.product_id === payload.product.id);
      if (existingItem) {
        existingItem.quantity = Math.min(existingItem.quantity + 1, 300);
      } else {
        state.newOrderCart.push({
          product_id: payload.product.id,
          quantity: 1,
          product: payload.product,
        });
      }
    },
    // Miqdorni to'g'ridan-to'g'ri o'rnatish (inputdan yozib kiritilganda),
    // 1..300 oralig'ida
    setNewOrderCartQuantity: (state, { payload }) => {
      const { product, quantity } = payload;
      const clamped = Math.max(1, Math.min(300, quantity));
      const existingItem = state.newOrderCart.find((item) => item.product_id === product.id);
      if (existingItem) {
        existingItem.quantity = clamped;
      } else {
        state.newOrderCart.push({
          product_id: product.id,
          quantity: clamped,
          product,
        });
      }
    },
    removeFromNewOrderCart: (state, { payload }) => {
      const existingItem = state.newOrderCart.find((item) => item.product_id === payload);
      if (existingItem && existingItem.quantity > 1) {
        existingItem.quantity -= 1;
      } else {
        state.newOrderCart = state.newOrderCart.filter((item) => item.product_id !== payload);
      }
    },
    clearNewOrderCart: (state) => {
      state.newOrderCart = [];
    },
    clearNewOrderErrors: (state) => {
      state.postOrderError = null;
      state.postOrderSuccess = false;
      state.error = '';
      state.success = false;
      state.patchOrderSuccess = false;
    },
    setPromotion: (state, action) => {
      state.promotion = action.payload;
      localStorage.setItem('promotion_id', action.payload);
    },
    removePromotion: () => {
      localStorage.removeItem('promotion_id');
    },
    setLocalPromotion: (state, action) => {
      const { itemId, promoId } = action.payload;
      const existing = state.localPromotions.find((p) => p.productId === itemId);
      if (existing) {
        existing.promoId = promoId;
      } else {
        state.localPromotions.push({ productId: itemId, promoId });
      }
    },
    setLocation: (state, action) => {
      const { latitude, longitude } = action.payload;
      state.location.latitude = latitude;
      state.location.longitude = longitude;
    },
    clearLocation: (state) => {
      state.location.latitude = null;
      state.location.longitude = null;
    },
    resetDeleted: (state) => {
      state.deleted = false;
    },
  },
});

export const {
  setOpenMenu,
  setCollapseId,
  setIsModalOpen,
  setOpenCollapse,
  setOrder,
  setOrderBy,
  setPage,
  setRowsPerPage,
  setSelected,
  setTab,
  setDefaults,
  setNewOrderModal,
  setIsOrderModalOpen,
  setOrderStatus,
  setOrderQuantity,
  setOrderId,
  setOrdersCount,
  setOrderDetailsModal,
  setNewOrderUserInfo,
  clearNewOrderUserInfo,
  clearNewOrderErrors,
  setNewOrderCart,
  addToNewOrderCart,
  setNewOrderCartQuantity,
  removeFromNewOrderCart,
  clearNewOrderCart,
  setOrderUser,
  setTotalPrice,
  setPromotion,
  removePromotion,
  setLocalPromotion,
  setClearLocalOrder,
  setLocation,
  clearLocation,
  resetDeleted,
} = ordersSlice.actions;

export default ordersSlice.reducer;
